// LicenseWall — full-page license activation screen shown when no valid license
// key is configured. Replaces the entire app shell until a key is activated.
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Tooltip,
  message,
} from "antd";
import {
  CopyOutlined,
  KeyOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { api, type LicenseStatus } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

// ── Installation ID display ────────────────────────────────────────────────

function InstallationIDBox({ id }: { id: string }) {
  const copy = () => {
    navigator.clipboard.writeText(id).then(
      () => message.success("Installation ID copied!"),
      () => message.error("Copy failed — select and copy manually"),
    );
  };
  return (
    <div
      style={{
        background: "color-mix(in srgb, var(--cyan) 6%, transparent)",
        border: "1px solid color-mix(in srgb, var(--cyan) 20%, transparent)",
        borderRadius: 10,
        padding: "14px 18px",
        marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cyan)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
        <KeyOutlined /> Installation ID
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <code
          style={{
            fontSize: 14,
            letterSpacing: 3,
            color: "var(--cyan-2)",
            userSelect: "all",
            flex: 1,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            background: "var(--bg-0)",
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid color-mix(in srgb, var(--cyan) 15%, transparent)",
          }}
        >
          {id}
        </code>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={copy}
          style={{
            background: "color-mix(in srgb, var(--cyan) 12%, transparent)",
            border: "1px solid color-mix(in srgb, var(--cyan) 25%, transparent)",
            color: "var(--cyan)",
            borderRadius: 6,
            flexShrink: 0,
          }}
        >
          Copy
        </Button>
      </div>
      <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
        Send this ID to the vendor to receive the license key for this installation.
      </div>
    </div>
  );
}

// ── Main wall ─────────────────────────────────────────────────────────────

interface LicenseWallProps {
  status: LicenseStatus;
  onActivated: (s: LicenseStatus) => void;
}

function LicenseWallCard({ status, onActivated }: LicenseWallProps) {
  const [key, setKey]       = useState("");
  const [saving, setSaving] = useState(false);
  const { user, logout }    = useAuth();
  const nav                 = useNavigate();

  const handleActivate = async () => {
    const trimmed = key.trim();
    if (!trimmed) {
      message.error("Please paste a license key into the box below");
      return;
    }
    setSaving(true);
    try {
      const updated = await api.activateLicense(trimmed);
      message.success("License activated successfully!");
      onActivated(updated);
    } catch (e) {
      if (e instanceof Error) message.error("Activation failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 560,
        background: "color-mix(in srgb, var(--bg-1) 85%, transparent)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid color-mix(in srgb, var(--cyan) 20%, transparent)",
        borderRadius: 16,
        padding: "32px 36px",
        boxShadow: "var(--shadow-hero), 0 0 60px color-mix(in srgb, var(--cyan) 6%, transparent)",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ marginBottom: 14 }}>
          <SafetyCertificateOutlined
            style={{ fontSize: 44, color: "var(--cyan)" }}
          />
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--strong)", marginBottom: 8 }}>
          Activate License
        </div>
        <div style={{ fontSize: 13, color: "var(--muted)" }}>
          {status.message?.includes("Trial expired")
            ? "Your trial period has ended. Please contact the vendor to receive a license key."
            : "No license key is configured yet. Please contact the vendor to receive one."}
        </div>
      </div>

      {/* Alert */}
      <Alert
        type="warning"
        showIcon
        message={status.message || "No license — system is blocked until activated."}
        style={{
          marginBottom: 20,
          fontSize: 13,
          background: "var(--tint-amber-bg)",
          border: "1px solid var(--tint-amber-bd)",
          borderRadius: 8,
          color: "var(--amber)",
        }}
      />

      {/* Installation ID */}
      {status.installation_id && (
        <InstallationIDBox id={status.installation_id} />
      )}

      {/* Key input */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
          Paste license key here:
        </div>
        <Input.TextArea
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Paste SFLOW-… license key here"
          rows={4}
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            marginBottom: 12,
            background: "color-mix(in srgb, var(--strong) 4%, transparent)",
            border: "1px solid color-mix(in srgb, var(--cyan) 15%, transparent)",
            borderRadius: 8,
            color: "var(--text)",
            resize: "vertical",
          }}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") handleActivate();
          }}
        />
        <Button
          type="primary"
          icon={<KeyOutlined />}
          onClick={handleActivate}
          loading={saving}
          disabled={!key.trim()}
          block
          size="large"
          style={{
            height: 44,
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            background: "linear-gradient(135deg, var(--cyan) 0%, var(--green) 100%)",
            border: "none",
            color: "var(--on-primary)",
            boxShadow: "0 8px 24px color-mix(in srgb, var(--cyan) 30%, transparent)",
          }}
        >
          Activate
        </Button>
      </div>

      {/* Footer: logged-in user + logout */}
      {user && (
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid color-mix(in srgb, var(--cyan) 12%, transparent)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--muted)" }}>
            Logged in as:{" "}
            <strong style={{ color: "var(--text)" }}>{user.username}</strong>{" "}
            <span style={{ color: "var(--cyan)", fontSize: 12 }}>({user.role})</span>
          </span>
          <Tooltip title="Logout">
            <Button
              size="small"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                background: "color-mix(in srgb, var(--red) 10%, transparent)",
                border: "1px solid color-mix(in srgb, var(--red) 20%, transparent)",
                color: "var(--red)",
                borderRadius: 6,
              }}
            >
              Logout
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

// ── Page wrapper (centered full screen) ──────────────────────────────────

export default function LicenseWall({
  status,
  onActivated,
}: LicenseWallProps) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(900px 600px at 80% -5%, color-mix(in srgb, var(--cyan) 10%, transparent), transparent 60%), " +
          "radial-gradient(800px 600px at -10% 40%, color-mix(in srgb, var(--green) 7%, transparent), transparent 60%), " +
          "var(--bg-0)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
      }}
    >
      {/* Brand */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 32 }}>
        <div
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(135deg, var(--cyan), var(--green))",
            borderRadius: 11,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 20px color-mix(in srgb, var(--cyan) 30%, transparent)",
          }}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="var(--on-primary)" strokeWidth="2.2" strokeLinecap="round">
            <path d="M2 12h4l3-7 4 14 3-7h6"/>
          </svg>
        </div>
        <span style={{ color: "var(--strong)", fontSize: 20, fontWeight: 700, letterSpacing: 0.3, fontFamily: "Inter, sans-serif" }}>
          NetScope Console
        </span>
      </div>

      <LicenseWallCard status={status} onActivated={onActivated} />
    </div>
  );
}

// ── LicenseGate: wraps the main Shell, shows LicenseWall when unlicensed ──

interface LicenseGateProps {
  children: React.ReactNode;
}

export function LicenseGate({ children }: LicenseGateProps) {
  const [status, setStatus]   = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.licenseStatus()
      .then(setStatus)
      .catch(() => {
        setStatus(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  if (status && !status.valid) {
    return (
      <LicenseWall
        status={status}
        onActivated={(updated) => setStatus(updated)}
      />
    );
  }

  return <>{children}</>;
}
